import { AbstractControl, ValidatorFn, ValidationErrors } from '@angular/forms';

/**
 * Cross-field validator for postal/zip code fields.
 * Reads the sibling US_or_CA control from the parent FormGroup to decide which format to enforce.
 * Pass getCountry() when the country control does not live in the same FormGroup as this control
 * (e.g. a split wizard form) — otherwise the default sibling lookup is used.
 *
 * Returns a rich ValidationErrors object so the pre-submit review panel can display
 * the correct message and offer a one-click Fix without any field-specific code in the components.
 *
 * Error shape:  { postalCodeFormat: { label, message, suggestedFix, canAutoFix } }
 */
export function postalCodeValidator(getCountry?: () => string): ValidatorFn {
  return (ctrl: AbstractControl): ValidationErrors | null => {
    const value = (ctrl.value as string) ?? '';
    if (!value) return null; // required validator handles empty fields

    const country = getCountry ? (getCountry() ?? '') : ((ctrl.parent?.get('US_or_CA')?.value as string) ?? '');
    const digits = value.replace(/\D/g, '');

    if (country === 'US') {
      if (/^\d{5}(-\d{4})?$/.test(value)) return null;
      const fix = digits.length === 5 ? digits : (digits.length === 9 ? `${digits.slice(0, 5)}-${digits.slice(5)}` : null);
      return {
        postalCodeFormat: {
          label: 'ZIP Code',
          message: 'US ZIP code must be 5 digits (12345) or ZIP+4 (12345-6789)',
          suggestedFix: fix,
          canAutoFix: !!fix,
        },
      };
    }

    if (country === 'Canada') {
      const canadaRegex = /^[ABCEGHJ-NPRSTVXY][0-9][ABCEGHJ-NPRSTV-Z]\s?[0-9][ABCEGHJ-NPRSTV-Z][0-9]$/i;
      if (canadaRegex.test(value)) return null;
      // Auto-fix only the unambiguous case: valid characters, just missing the space.
      const compact = value.replace(/\s/g, '');
      const compactRegex = /^[ABCEGHJ-NPRSTVXY][0-9][ABCEGHJ-NPRSTV-Z][0-9][ABCEGHJ-NPRSTV-Z][0-9]$/i;
      const fix = compactRegex.test(compact) ? `${compact.slice(0, 3)} ${compact.slice(3)}` : null;
      return {
        postalCodeFormat: {
          label: 'Postal Code',
          message: 'Canadian postal code must be in A1A 1A1 format',
          suggestedFix: fix,
          canAutoFix: !!fix,
        },
      };
    }

    return null; // country not yet selected — skip
  };
}
